import React from "react";

import { DashboardEditorProps } from "components/ComponentType";
import { DatasetChartProps, DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import TimeUnitDropdown from "../dropdowns/time-unit-dropdown/TimeUnitDropdown";
import InputLabelWrapper from "../input-label-wrapper/InputLabelWrapper";

const DatasetChartConfig: React.FC<DashboardEditorProps<DatasetChartProps>> = ({
  config,
  onConfigChange,
}) => {
  const handleLoadConfChange = (field: keyof DatasetLoadingConf, value: string | number | null) => {
    if (value === null && config.loadConf) {
      const { [field]: remove, ...rest } = config.loadConf;
      onConfigChange({ ...config, loadConf: rest as DatasetLoadingConf });
      return;
    }
    onConfigChange({
      ...config,
      loadConf: { ...config?.loadConf, [field]: value! } as DatasetLoadingConf,
    });
  };

  const handleChange = (
    field: keyof DatasetChartProps,
    value: string | string[] | number | boolean | null
  ) => {
    if (value === null) {
      const { [field]: remove, ...rest } = config;
      onConfigChange({ ...(rest as DatasetChartProps) });
      return;
    }
    onConfigChange({ ...config, [field]: value });
  };

  return (
    <Accordion className="w-full">
      <AccordionTab header="Component settings">
        <div className="grid p-fluid">
          <InputLabelWrapper labelName="Lag Offset" col={3}>
            <div className="p-inputgroup">
              <InputNumber
                value={config?.loadConf?.lag ?? 6}
                style={{ flexGrow: "0.5" }}
                onValueChange={e => handleLoadConfChange("lag", e.value)}
              />
              <TimeUnitDropdown
                className="p-inputgroup-addon p-0"
                value={config?.loadConf?.lagUnit ?? "HOURS"}
                onChange={e => handleLoadConfChange("lagUnit", e.value?.toString().toUpperCase())}
              />
            </div>
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Lead Offset" col={3}>
            <div className="p-inputgroup">
              <InputNumber
                value={config?.loadConf?.lead ?? 0}
                style={{ flexGrow: "0.5" }}
                onValueChange={e => handleLoadConfChange("lead", e.value)}
              />
              <TimeUnitDropdown
                className="p-inputgroup-addon p-0 "
                value={config?.loadConf?.leadUnit ?? "HOURS"}
                onChange={e => handleLoadConfChange("leadUnit", e.value?.toString().toUpperCase())}
              />
            </div>
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Align Unit" col={2}>
            <TimeUnitDropdown
              value={config?.loadConf?.alignUnit ?? "MINUTES"}
              onChange={e => handleLoadConfChange("alignUnit", e.value?.toString().toUpperCase())}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Refresh Time" col={2}>
            <InputNumber
              value={config?.updateFrequency ?? 0}
              onChange={e => handleChange("updateFrequency", e.value)}
              suffix=" s"
              showButtons
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Time Axis Step" col={2}>
            <InputNumber
              value={config?.timeAxisStepSeconds}
              onChange={e => handleChange("timeAxisStepSeconds", e.value)}
              suffix=" s"
              showButtons
            />
          </InputLabelWrapper>
          
          <InputLabelWrapper labelName="Height" col={2}>
            <InputNumber
              value={config?.height as number ?? 400}
              suffix=" px"
              onChange={e => handleChange("height", e.value)}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Width" col={2}>
            <InputNumber
              value={config?.width as number}
              suffix=" px"
              onChange={e => handleChange("width", e.value)}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Dynamic Height" col={2} className="align-items-center gap-3">
            <InputSwitch
              checked={config?.dynamicHeight}
              onChange={e => handleChange("dynamicHeight", e.value)}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Show Date" col={2} className="align-items-center gap-3">
            <InputSwitch
              checked={config?.showDateSelection ?? true}
              onChange={e => handleChange("showDateSelection", e.value)}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Hide Refresh" col={2} className="align-items-center gap-3">
            <InputSwitch
              checked={config?.hideRefresh}
              onChange={e => handleChange("hideRefresh", e.value)}
            />
          </InputLabelWrapper>
        </div>
      </AccordionTab>
    </Accordion>
  );
};

export default DatasetChartConfig;
